/**
 * Catálogo de perfiles de monitor CRT.
 *
 * Cada perfil define el "look" del fósforo de la pantalla — paleta,
 * fuente y triplas RGB de glow. Aplicar un perfil consiste en escribir
 * `data-profile="<id>"` en `<html>`; el CSS resuelve las variables
 * desde el bloque `[data-profile="<id>"]` correspondiente en
 * `terminal.css`.
 *
 * Catálogo (cerrado en sesión 2026-05-11):
 *  - mono-p1   verde fósforo P1 (IBM 5151, 1981) — DEFAULT
 *  - mono-p3   ámbar P3 (Hazeltine/Wyse, 1981)
 *  - cga       IBM CGA Palette 1 (cian/magenta/blanco, 1981)
 *  - amstrad   Amstrad CPC Mode 1 (amarillo sobre azul cobalto, 1984)
 *  - ega       IBM EGA Sierra/Norton (azul oscuro + blanco, 1984)
 *  - vga       IBM VGA texto 80x25 (azul oscuro + cian, 1987)
 *
 * SVGA descartado: con 65k colores se parecía demasiado a una web
 * moderna y perdía la gracia retro.
 */

/** Identificador de un perfil de monitor. */
export type MonitorProfileId = 'mono-p1' | 'mono-p3' | 'cga' | 'amstrad' | 'ega' | 'vga';

/**
 * Definición declarativa de un perfil. La aplicación visual es 100% CSS
 * (variables bajo `[data-profile="<id>"]` en `terminal.css`). Este
 * objeto sirve como registro canónico — nombre legible, era histórica
 * y descripción para el selector del CRT.
 */
export interface MonitorProfile {
  /** Identificador único (slug). */
  readonly id: MonitorProfileId;
  /** Nombre legible en el selector del CRT (ALL CAPS, estilo terminal). */
  readonly label: string;
  /** Año + referencia histórica (para el subtítulo del selector). */
  readonly era: string;
  /** Descripción técnica corta (paleta, fuente, contexto). */
  readonly description: string;
  /**
   * URL relativa al sample de ambiente en bucle. Si `null` o ausente,
   * el audio engine reproduce el ambiente sintético clásico (coil
   * whine + hum + ventilador). En 12a-3 solo Amstrad lo usa
   * (`/sounds/amstrad-ambient.ogg`).
   *
   * El asset es gitignored (`**\/*.ogg` en `.gitignore`); en producción
   * hay que subirlo al VPS o servirlo desde R2 igual que /music/.
   */
  readonly ambientSample?: string | null;
}

/**
 * Orden de presentación en el selector (cronológico, default primero):
 * verdes fósforo monocromos → CGA → Amstrad → EGA → VGA.
 */
const PROFILE_ORDER: readonly MonitorProfileId[] = [
  'mono-p1',
  'mono-p3',
  'cga',
  'amstrad',
  'ega',
  'vga',
];

const PROFILES: Readonly<Record<MonitorProfileId, MonitorProfile>> = {
  'mono-p1': {
    id: 'mono-p1',
    label: 'MONOCROMO VERDE',
    era: 'IBM 5151 fósforo P1',
    description: 'Monitor monocromo P1. Verde sobre negro. El clásico de terminales DOS.',
  },
  'mono-p3': {
    id: 'mono-p3',
    label: 'MONOCROMO ÁMBAR',
    era: 'Hazeltine ámbar P3',
    description: 'Monitor monocromo P3. Ámbar sobre negro. Estilo Wyse/Hazeltine.',
  },
  cga: {
    id: 'cga',
    label: 'CGA',
    era: 'IBM 1981 (4 colores)',
    description: 'IBM Color Graphics Adapter. Palette 1 high-intensity: cian + blanco sobre magenta.',
  },
  amstrad: {
    id: 'amstrad',
    label: 'AMSTRAD CPC',
    era: 'Amstrad 1984',
    description: 'Amstrad CPC Mode 1 (4 colores). Amarillo sobre azul cobalto. Fuente Amstrad CPC464.',
    ambientSample: '/sounds/amstrad-ambient.ogg',
  },
  ega: {
    id: 'ega',
    label: 'EGA',
    era: 'IBM 1984 (16 colores)',
    description: 'IBM Enhanced Graphics Adapter. Look Sierra/Norton: blanco brillante sobre azul oscuro.',
  },
  vga: {
    id: 'vga',
    label: 'VGA',
    era: 'IBM 1987 (256 colores)',
    description: 'IBM Video Graphics Array texto 80x25. Cian sobre azul oscuro, estilo "moderno retro".',
  },
};

/** Default fábrica — usado si no hay preferencia guardada en localStorage. */
export const DEFAULT_PROFILE_ID: MonitorProfileId = 'mono-p1';

/** Type guard runtime (para validar valores leídos de localStorage). */
export function isMonitorProfileId(value: unknown): value is MonitorProfileId {
  return typeof value === 'string' && value in PROFILES;
}

/** Devuelve la definición de un perfil por id. Lanza si el id no existe. */
export function getProfile(id: MonitorProfileId): MonitorProfile {
  const profile = PROFILES[id];
  if (!profile) {
    throw new Error(`[auth] perfil de monitor desconocido: ${String(id)}`);
  }
  return profile;
}

/** Lista todos los perfiles disponibles, en orden cronológico de presentación. */
export function listProfiles(): readonly MonitorProfile[] {
  return PROFILE_ORDER.map((id) => PROFILES[id]);
}

/**
 * Aplica un perfil al documento escribiendo `data-profile` en `<html>`.
 * El CSS lee ese atributo para resolver las variables `--phos*`. Llamar
 * a esto antes del primer paint del terminal evita parpadeos.
 */
export function applyProfile(id: MonitorProfileId): void {
  const profile = getProfile(id);
  document.documentElement.dataset.profile = profile.id;
}
